<script lang="ts">
  // The Activity Reports page (/social-reports): the default design for each report type, and
  // downloads for any recent activity to share on Instagram and other platforms. Reports sent to
  // Strava automatically use the saved designs; the Send to Strava dialog starts from them.
  import { onMount } from 'svelte'
  import { getSocialReportPreview, type SocialReportActivity } from '@/Util/api'
  import { ErrorCode } from '@/Util/errorCodes'
  import { notifyApiError } from '@/Util/notify'
  import GroupReportPrivacy from './GroupReportPrivacy.svelte'
  import ReportDesigner from './ReportDesigner.svelte'
  import { withSamples } from './samples'
  import type { ReportData, ReportSettings, ReportType } from './types'

  const ALL_TYPES: ReportType[] = ['breakthrough', 'xfai', 'workout', 'group', 'activity']

  let preview: { data: ReportData, settings: ReportSettings, path: string | null, real: ReportData | null, types: ReportType[] } | null = null
  let activities: SocialReportActivity[] = []
  let selected = ''
  let switching = false
  let error: string | null = null
  let showOnGroupReports: boolean | null = null

  // Stock backgrounds for the sample ride, when the user has no activity to preview.
  let sampleStock: string[] = []

  // Every report type can be designed here, whatever the activity has.
  function previewFrom(data: ReportData | null, settings: ReportSettings) {
    return {
      data: { ...withSamples(data, sampleStock), types: ALL_TYPES, recommended_type: data?.recommended_type ?? 'breakthrough' } as ReportData,
      settings,
      path: data?.activity.path ?? null,
      real: data,
      types: data?.types ?? [],
    }
  }

  onMount(async () => {
    const res = await getSocialReportPreview()
    if (!res.success) {
      error = res.message ?? 'Could not load your report designs.'
      return
    }
    activities = res.activities ?? []
    showOnGroupReports = res.show_on_group_reports
    sampleStock = res.sample_stock_backgrounds ?? []
    selected = res.data?.activity.path ?? ''
    preview = previewFrom(res.data, res.settings)
  })

  async function pick(path: string) {
    if (!preview || !path) return
    switching = true
    const res = await getSocialReportPreview(path)
    switching = false
    if (!res.success) {
      notifyApiError(res, { code: ErrorCode.SOCIAL_REPORT_DATA_FAILED, message: 'Could not load that activity.' })
      selected = preview.path ?? ''
      return
    }
    // The designer keeps its own copy of the settings, so only the activity changes.
    preview = previewFrom(res.data, preview.settings)
  }

  const dateFormat = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
</script>

<div class="mx-auto max-w-[1400px] px-4 pb-6">
  <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
    <p class="m-0 max-w-[720px] text-sm text-muted-foreground">
      Design report images to share your activities on Strava, Instagram and anywhere else. Pick a design for each
      report type and save it: reports Xert adds to your Strava activities use your saved design. Download or share a
      report for any recent activity.
    </p>
    {#if activities.length}
      <label class="m-0 flex min-w-0 flex-col gap-1 text-xs font-medium text-muted-foreground lg:w-[340px]">
        Activity
        <span class="flex items-center gap-2">
          <select bind:value={selected} on:change={() => pick(selected)} disabled={switching}
            class="h-9 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-sm font-normal text-foreground">
            {#each activities as a (a.path)}
              <option value={a.path}>{a.name || 'Untitled activity'} · {dateFormat.format(new Date(a.start_date))}</option>
            {/each}
          </select>
          {#if switching}<i class="fa-solid fa-spinner-third fa-spin text-gray-400" />{/if}
        </span>
      </label>
    {/if}
  </div>

  {#if showOnGroupReports !== null}
    <div class="mb-6 max-w-[720px] rounded-lg border border-border px-4">
      <GroupReportPrivacy bind:checked={showOnGroupReports} />
    </div>
  {/if}

  {#if error}
    <div class="text-sm text-red-600">{error}</div>
  {:else if preview}
    <ReportDesigner mode="page" {preview} />
  {:else}
    <div class="flex items-center gap-2 text-sm">
      <i class="fa-solid fa-spinner-third fa-spin text-gray-400" />
      Loading your report designs...
    </div>
  {/if}
</div>
